import { unwrapResult } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import * as React from 'react';
import DatePicker from "react-datepicker";
import InputMask from 'react-input-mask';
import Select from 'react-select';
import { Button, Col, Collapse, Form, FormFeedback, FormGroup, FormText, Input, Label, Nav, NavItem, NavLink, Row, Spinner, TabContent, TabPane } from 'reactstrap';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { useDebounce } from 'use-debounce';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
    DatabaseEntityContact,
    EntityContactRole, EntityUserRole,
    ReportAccessEnum,
    clearState,
    deauthorizeEntityContact,
    getEntityContactByEmail,
    getEntityContacts,
    saveEntityContact, selectCurrentEntityCode, selectDatabaseContacts, setEntityCode, setEntityContactEndDate
} from '../../redux/ContactSlice';
import { EntityAuthorization, EntityReportAccessChange, selectedEntity } from '../../redux/EntitySlice';
import { NewSignupUser, registerUser } from '../../redux/RegistrationSlice';
import { EntityAuthorization as UserEntityAuthorization, UserRoleEnum, selectUserEntityAuthorizations, selectUserRole } from '../../redux/UserSlice';
import { getUserDataByEmail } from '../../redux/UsersSlice';
import dateHelper from '../../utils/dateHelper';
import utilityHelper from '../../utils/utilityHelper';
import CanShowGivenAfrReportAccess from './CanShowGivenAfrReportAccess';
import CanShowGivenAuthorizedContactUserRoles from './CanShowGivenAuthorizedContactUserRoles';
import EntityAuthorizationSyncer from './EntityAuthorizationSyncer';
import { isAdmin } from '../../redux/MiscSlice';
import EntityDeauthorizationSyncer from './EntityDeauthorizationSyncer';





export interface EntityContactsManagementOptions {
    isStaff: boolean;
}


const EntityContactsManager = (props: EntityContactsManagementOptions) => {

    const dispatch = useAppDispatch();
    const entitySelector = useAppSelector(selectedEntity);
    const currentEntityCodeSelector = useAppSelector(selectCurrentEntityCode);
    const databaseContactsSelector = useAppSelector(selectDatabaseContacts);
    const authorizedEntitiesSelector: Array<UserEntityAuthorization> | null = useAppSelector(selectUserEntityAuthorizations);
    const userRoleSelector = useAppSelector(selectUserRole);
    const isAdminUser: boolean = useAppSelector(isAdmin);

    //to be source from mutations
    var userRoleOptions = [
        { label: "----", value: "" },
        { label: "Editor", value: "EDITOR" },
        { label: "Third Party", value: "THIRD_PARTY" },
        { label: "Notification", value: "NOTIFICATION" },
    ];

    //to be source from mutations
    var contactRoleOptions = [
        { label: "----", value: "" },
        { label: "Primary", value: "PRIMARY" },
        { label: "Secondary", value: "SECONDARY" },
        { label: "Auditor", value: "AUDITOR" },
        { label: "Contact", value: "CONTACT" },
        { label: "Management Company", value: "MANAGEMENT_COMPANY" },
        { label: "Other", value: "OTHER" },
    ];

    const defaultValue = { label: "----", value: "" };

    //editing mode
    const [editMode, setEditMode] = React.useState<"LANDING" | "LANDING_STAFF" | "CREATE_USER" | "CREATE_STAFF" | "EDIT_USER" | "EDIT_STAFF">(props.isStaff ? 'LANDING_STAFF' : 'LANDING');
    const [canEditEntity, setCanEditEntity] = React.useState<boolean>(false);

    //Local Government User
    const [firstName, setFirstName] = React.useState<string>("");
    const [lastName, setLastName] = React.useState<string>("");
    const [phoneNumber, setPhoneNumber] = React.useState<string>("");
    const [phoneExtension, setPhoneExtension] = React.useState<string>("");
    const [email, setEmail] = React.useState<string>("");
    const [confirmEmail, setConfirmEmail] = React.useState<string>("");
    const [emailValue] = useDebounce(email, 2000);
    const [startDate, setStartDate] = React.useState<any>();
    const [endDate, setEndDate] = React.useState<any>();


    //User Assignment Info
    const [title, setTitle] = React.useState<string>("");

    const [contactRoles] = React.useState(contactRoleOptions);
    const [userRoles] = React.useState(userRoleOptions);

    const [selectedContactRoleAfr, setSelectedContactRoleAfr] = React.useState(defaultValue);
    const [selectedUserRoleAfr, setSelectedUserRoleAfr] = React.useState(defaultValue);

    const [selectedContactRole939, setSelectedContactRole939] = React.useState(defaultValue);
    const [selectedUserRole939, setSelectedUserRole939] = React.useState(defaultValue);

    const [selectedContactRole29, setSelectedContactRole29] = React.useState(defaultValue);
    const [selectedUserRole29, setSelectedUserRole29] = React.useState(defaultValue);

    const [entityContact, setEntityContact] = React.useState<DatabaseEntityContact | null>(null);

    const [afrReportAccess, setAfrReportAccess] = React.useState<boolean>(false);
    const [courtReporting29ReportAccess, setCourtReporting29ReportAccess] = React.useState<boolean>(false);
    const [courtReporting939ReportAccess, setCourtReporting939ReportAccess] = React.useState<boolean>(false);

    const [afrHasPrimaryEditor, setAfrHasPrimaryEditor] = React.useState<boolean>(false);
    const [courtReporting939HasPrimaryEditor, setCourtReporting939HasPrimaryEditor] = React.useState<boolean>(false);
    const [courtReporting29HasPrimaryEditor, setCourtReporting29HasPrimaryEditor] = React.useState<boolean>(false);

    //state variables
    //TODO cleanup the fade in hooks that aren't being used
    const [canEdit, setCanEdit] = React.useState<boolean>(true);
    const [confirmEmailFadeIn, setConfirmEmailFadeIn] = React.useState<boolean>(false);

    //entity contact address
    const [street, setStreet] = React.useState<string | null>(null);
    const [zipCode, setZipCode] = React.useState<string | null>(null);
    const [pobox, setPobox] = React.useState<string | null>(null);
    const [state, setState] = React.useState<string | null>(null);
    const [county, setCounty] = React.useState<string | null>(null);
    const [cityCode, setCityCode] = React.useState<string | null>(null);

    const [isLoadingUserData, setIsLoadingUserData] = React.useState<boolean>(false);
    const [afrEnabled, setAfrEnabled] = React.useState<boolean>(true);
    const [courtReporting939Enabled, setCourtReporting939Enabled] = React.useState<boolean>(true);
    const [courtReporting29Enabled, setCourtReporting29Enabled] = React.useState<boolean>(true);
    const [usageMsg, setUsageMsg] = React.useState<string>(props.isStaff
        ? "Please select an existing staff member or create a new staff member to appear in reports."
        : "Please select an existing user or create a new user to review report access data."
     );
    const [activeTab, setActiveTab] = React.useState('1');
    const toggle = (tab: any) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    var tabulatorRef = React.useRef<any>(null);

    // Hierarchical organization
    const [contactListData, setContactListData] = React.useState<any[]>([]);


    //Entity Syncers
    const [entitySyncerIsOpen, setEntitySyncerIsOpen] = React.useState<boolean>(false);
    const [contactChangeValue, setContactChangeValue] = React.useState<DatabaseEntityContact>();
    const [entityDeauthorizationSyncerIsOpen, setEntityDeauthorizationSyncerIsOpen] = React.useState<boolean>(false);

    //Authorization 

    const contactSortKey = function (contact: any) : string {
        var dateString: string = new Date().toISOString();
        var endDateString: string = "9999";
        if (contact.startDate) {
            var cdate = dateHelper.convertToDate(contact.startDate);
            if (cdate) {
                dateString = cdate.toISOString();
            }
        }
        if (contact.endDate) {
            var cedate = dateHelper.convertToDate(contact.endDate);
            if (cedate) {
                endDateString = cedate.toISOString();
                dateString = " " + dateString;
            }
        }
        var result = dateString + endDateString + contact.title.toLowerCase() + " " + contact.lastName.toLowerCase();
        return result;
    }
    const processEntityContacts = function (rawList: any[]): any[] {

        setAfrHasPrimaryEditor(false);
        setCourtReporting939HasPrimaryEditor(false);
        setCourtReporting29HasPrimaryEditor(false);

        for (var i = 0; i < rawList.length; i++) {

            for (var j = 0; j < rawList[i].entityAuthorizations.length; j++) {

                if (rawList[i].endDate == null) {

                    if (rawList[i].entityAuthorizations[j].entityUserRole == EntityUserRole.EDITOR
                        && rawList[i].entityAuthorizations[j].entityContactRole == EntityContactRole.PRIMARY) {
                        switch (rawList[i].entityAuthorizations[j].reportAccess) {

                            case ReportAccessEnum.AFR:
                                setAfrHasPrimaryEditor(true);
                                break;

                            case ReportAccessEnum.NINE_THIRTY_NINE:
                                setCourtReporting939HasPrimaryEditor(true);
                                break;

                            case ReportAccessEnum.TWENTY_NINE:
                                setCourtReporting29HasPrimaryEditor(true);
                                break;

                            default:
                                break;
                        }
                    }
                }
            }
        }


        if (props.isStaff) {
            rawList.sort((a, b) => contactSortKey(a).localeCompare(contactSortKey(b)) * -1);
        }
        var results: any[] = [];
        var userMap: any = {};
        // Initially organize by email. The entries will still be in the order supplied by the server
        rawList.map(contact => {
            var id = contact.email;
            if (!userMap.hasOwnProperty(id)) {
                userMap[id] = [];
            }
            var userHistory = userMap[id];
            if (props.isStaff) {
                userHistory.unshift(contact);
            } else {
                userHistory.push(contact);
            }
        });
        // Now take the last entry in each list of the email partition sets, make that the current, an assign the remainder as children
        Object.keys(userMap).map(key => {
            var hist = userMap[key];
            if (hist.length === 1) {
                results.push(hist[0]);
            } else {
                var currentContact = hist.pop();
                currentContact._children = hist;
                results.push(currentContact);
            }
        });

        

        if (userRoleSelector == UserRoleEnum.EntityUser) {

            var filteredResults: any[] = [];

            for (var i = 0; i < results.length; i++) {
                if (results[i].endDate == null) {
                    for (var j = 0; j < results[i].entityAuthorizations.length; j++) {
                        switch (results[i].entityAuthorizations[j].reportAccess) {
                            case ReportAccessEnum.AFR:
                                if (afrEnabled == true) {
                                    filteredResults.push(results[i]);
                                }
                                break;

                            case ReportAccessEnum.NINE_THIRTY_NINE:
                                if (courtReporting939Enabled == true) {
                                    filteredResults.push(results[i]);
                                }
                                break;

                            case ReportAccessEnum.TWENTY_NINE:
                                if (courtReporting29Enabled == true) {
                                    filteredResults.push(results[i]);
                                }
                                break;
                            default:
                                break;
                        }
                    }
                }
                             
            }
            results = [...new Set(filteredResults)];
        }


        return results;
    }

    const reportAccessFormatter = function (cell: any, formatterParams: any, onRendered: any) {
        var cellValue: DatabaseEntityContact = cell._cell.row.data;
        var spanElement = document.createElement('span');
        if (cellValue != null) {

            if (cellValue.entityAuthorizations != null
                && cellValue.entityAuthorizations.length > 0) {
                var name = '';
                var reportAccessType = '';
                for (var i = 0; i < cellValue.entityAuthorizations.length; i++) {

                    switch (cellValue.entityAuthorizations[i].reportAccess) {
                        case ReportAccessEnum.AFR:
                            reportAccessType = "AFR";
                            break;
                        case ReportAccessEnum.NINE_THIRTY_NINE:
                            reportAccessType = "939.185";
                            break;
                        case ReportAccessEnum.TWENTY_NINE:
                            reportAccessType = "29.0085";
                            break;
                        default:
                            reportAccessType = '';
                            break;
                    }

                    name += reportAccessType;
                    if (i < cellValue.entityAuthorizations.length - 1) {
                        name += ', ';
                    }
                }
                spanElement.appendChild(document.createTextNode(name));
            }
        }

        return spanElement;
    };



    // ************************************************************* /*
    // Grid Options                                                  //
    // ************************************************************* /*
    const contactGridOptions = {
        layout: "fitColumns",
        dataTree: true,
        dataTreeStartExpanded: false
    }


    const defaultColumns = [
        { title: 'First Name', field: 'firstName' },
        { title: 'Last Name', field: 'lastName' },
        { title: 'Title', field: 'title' },
        { title: 'Email', field: 'email' },
        {
            title: 'Report Access', field: 'reportAccess', formatter: reportAccessFormatter
        },
        {
            title: 'Start', field: 'startDate', formatter: dateHelper.tabulatorDateFormatter(),
        },
        {
            title: 'End', field: 'endDate', formatter: dateHelper.tabulatorDateFormatter(),
        },
    ];

    const staffColumns = [
        { title: 'First Name', field: 'firstName' },
        { title: 'Last Name', field: 'lastName' },
        { title: 'Title', field: 'title' },
        { title: 'Start', field: 'startDate', formatter: dateHelper.tabulatorDateFormatter() },
        { title: 'End', field: 'endDate', formatter: dateHelper.tabulatorDateFormatter() }
    ];

    const handleContactStartDate = function (event: any) {

        if (event && isNaN(Date.parse(event)) == false) {
            var dateString = dateHelper.formatDateToISO(event);
            if (dateString != "huh") {
                setStartDate(new Date(dateString));
            }
        } else {
            setStartDate(null);
        }

    }

    const handleContactEndDate = function (event: any) {

        if (event && isNaN(Date.parse(event)) == false) {
            var dateString = dateHelper.formatDateToISO(event);
            if (dateString != "huh") {
                setEndDate(new Date(dateString));
            }
        } else {
            setEndDate(null);
        }

    }




    const handleSaveButtonClick = async (isExistingUser: boolean) => {
        if (entitySelector &&
            entityContact) {

            if (IsValidForm()) {
                let entityToSave: DatabaseEntityContact = getBlankEntity();

                //convert report access to enum array
                var reportAccessesArray = Object.values(ReportAccessEnum);
                let reportAccessIsChecked: boolean = false;
                let entityContactRoleKey: string = "";
                let entityUserRoleKey: string = "";


                for (var i = 0; i < reportAccessesArray.length; i++) {
                    switch (reportAccessesArray[i]) {
                        case ReportAccessEnum.AFR:
                            reportAccessIsChecked = afrReportAccess;
                            entityContactRoleKey = selectedContactRoleAfr.value;
                            entityUserRoleKey = selectedUserRoleAfr.value;
                            break;
                        case ReportAccessEnum.NINE_THIRTY_NINE:
                            reportAccessIsChecked = courtReporting939ReportAccess;
                            entityContactRoleKey = selectedContactRole939.value;
                            entityUserRoleKey = selectedUserRole939.value;
                            break;
                        case ReportAccessEnum.TWENTY_NINE:
                            reportAccessIsChecked = courtReporting29ReportAccess;
                            entityContactRoleKey = selectedContactRole29.value;
                            entityUserRoleKey = selectedUserRole29.value;
                            break;
                        default:
                            break;
                    }

                    if (reportAccessIsChecked) {
                        let authorization: EntityAuthorization = {
                            entityContactRole: EntityContactRole[entityContactRoleKey as keyof typeof EntityContactRole],
                            entityUserRole: EntityUserRole[entityUserRoleKey as keyof typeof EntityUserRole],
                            reportAccess: reportAccessesArray[i],
                            startDate: DateTime.fromISO(),
                            endDate: null
                        };

                        entityToSave.entityAuthorizations.push(authorization);
                    }
                }

                if (street || pobox || cityCode || zipCode) {
                    entityToSave.street = street;
                    entityToSave.pobox = pobox;
                    entityToSave.cityCode = cityCode;
                    entityToSave.zip = zipCode;
                    entityToSave.addressId = entityContact.addressId;
                }

                entityToSave.entityCode = currentEntityCodeSelector;
                entityToSave.entityContactId = entityContact.entityContactId;
                entityToSave.title = title;
                entityToSave.startDate =entityContact.startDate;
                entityToSave.endDate = isExistingUser ? entityContact.endDate : null;
                entityToSave.logerUserId = entityContact.logerUserId;
                entityToSave.userName = entityContact.userName;
                entityToSave.firstName = firstName;
                entityToSave.lastName = lastName;
                entityToSave.contactInfoId = entityContact.contactInfoId;
                entityToSave.email = email;
                entityToSave.phoneNumber = phoneNumber;
                entityToSave.extension = phoneExtension;

                if (isExistingUser) {
                    await dispatch(setEntityContactEndDate(entityToSave))
                }

                await dispatch(saveEntityContact(entityToSave));

                if (isAdminUser == true) {
                    setContactChangeValue(entityToSave);
                    setEntitySyncerIsOpen(true);
                }
                
                clearInputState();
                setEditMode('LANDING');
            }
        }
    }
    const convertToDateInput = function(d: Date | null) : string | null {
        if (d) {
            return d.toISOString();
        }
        return null;
    }
    const handleSaveStaffButtonClick = async (isExistingUser: boolean) => {
        if (entitySelector &&
            entityContact) {

            if (IsValidStaffForm()) {
                var emailToUse: string = createStaffEmail();
                setEmail(emailToUse);

                let entityToSave: DatabaseEntityContact = getBlankEntity();


                entityToSave.entityCode = currentEntityCodeSelector;
                entityToSave.entityContactId = entityContact.entityContactId;
                entityToSave.title = title;
                entityToSave.startDate = convertToDateInput(startDate);
                entityToSave.endDate = convertToDateInput(endDate);
                entityToSave.logerUserId = entityContact.logerUserId;
                entityToSave.userName = entityContact.userName;
                entityToSave.firstName = firstName;
                entityToSave.lastName = lastName;
                entityToSave.contactInfoId = entityContact.contactInfoId;
                entityToSave.email = emailToUse;
                entityToSave.phoneNumber = phoneNumber;
                entityToSave.extension = phoneExtension;

                if (isExistingUser) {
                    await dispatch(setEntityContactEndDate(entityToSave))
                }

                await dispatch(saveEntityContact(entityToSave));
                if (!isExistingUser) {
                    await dispatch(getEntityContacts(entitySelector.code));
                    await dispatch(setEntityCode(entitySelector.code));
                }
                clearInputState();
                setEditMode('LANDING_STAFF');
            }
        }
    }

    const handleAddContactClick = async () => {
        if (entitySelector &&
            entityContact) {
            if (IsValidForm()) {
                var emailToUse : string = email;
                setIsLoadingUserData(true);
                let newUser: NewSignupUser = {
                    firstName: firstName,
                    lastName: lastName,
                    eMail: emailToUse,
                    role: UserRoleEnum.EntityUser
                };
                await dispatch(registerUser(newUser));
                await handleSaveButtonClick(false);
                setIsLoadingUserData(false);
            }
        }
    }
    const createStaffEmail = function () {
        // Note - we only take the first word of the last name to exclude credentials (like CPA).
        var emailToUse: string = firstName + "." + lastName.replace(" ", "_").replace("(", "").replace(")", "") + "@dfsstaff.com";
        return emailToUse;
    }
    const handleAddStaffClick = async () => {
        if (entitySelector &&
            entityContact) {
            if (IsValidStaffForm()) {
                await handleSaveStaffButtonClick(false);
            }
        }
    }

    const handleCancelClick = async () => {
        clearInputState();
        if (props.isStaff) {
            setEditMode('LANDING_STAFF');
        } else {
            setEditMode('LANDING');
        }
    }

    const handleDeauthorizeClick = async () => {
        if (entityContact != null) {
            var result: { item1: string | null, item2: string | null };
            result = { item1: entityContact.email, item2: entityContact.entityCode !== null ? entityContact.entityCode.toString() : "" }
            await dispatch(deauthorizeEntityContact(result));

            if (isAdminUser == true) {
                setEntityDeauthorizationSyncerIsOpen(true);
            }

            dispatch(getEntityContacts(entitySelector.code));
            dispatch(setEntityCode(entitySelector.code));
            clearInputState();
            setEditMode('LANDING');
        }
    }

    const handleDeauthorizeStaffClick = async () => {
        if (entityContact != null) {
            let entityToSave: DatabaseEntityContact = getBlankEntity();
            var staffEmail = createStaffEmail();

            entityToSave.entityCode = currentEntityCodeSelector;
            entityToSave.entityContactId = entityContact.entityContactId;
            entityToSave.title = title;
            entityToSave.startDate = entityContact.startDate;
            entityToSave.endDate = DateTime.fromISO();
            entityToSave.logerUserId = entityContact.logerUserId;
            entityToSave.userName = entityContact.userName;
            entityToSave.firstName = firstName;
            entityToSave.lastName = lastName;
            entityToSave.contactInfoId = entityContact.contactInfoId;
            entityToSave.email = staffEmail;

            await dispatch(setEntityContactEndDate(entityToSave))

            await dispatch(saveEntityContact(entityToSave));
            dispatch(getEntityContacts(entitySelector.code));
            dispatch(setEntityCode(entitySelector.code));
            clearInputState();
            setEditMode('LANDING_STAFF');
        }
    }
    const handleCreateStaffClick = async () => {
        let entityToSave: DatabaseEntityContact = getBlankEntity();
        var staffEmail = createStaffEmail();

        entityToSave.entityCode = currentEntityCodeSelector;
        entityToSave.entityContactId = null;
        entityToSave.title = title;
        if (startDate) {
            entityToSave.startDate = DateTime.fromISO(startDate);
        } else {
            entityToSave.startDate = DateTime.fromISO();
        }
        entityToSave.endDate = null;
        entityToSave.logerUserId = null;
        entityToSave.userName = null;
        entityToSave.firstName = firstName;
        entityToSave.lastName = lastName;
        entityToSave.contactInfoId = null;
        entityToSave.email = staffEmail;

        await dispatch(saveEntityContact(entityToSave));
        dispatch(getEntityContacts(entitySelector.code));
        dispatch(setEntityCode(entitySelector.code));
        clearInputState();
        setEditMode('LANDING_STAFF');
    }

    const getBlankEntity = () => {
        let newEntityContact: DatabaseEntityContact = {
            addressId: null,
            startDate: null,
            endDate: null,
            cityCode: null,
            contactInfoId: null,
            email: null,
            entityCode: null,
            entityContactId: null,
            extension: null,
            firstName: null,
            lastName: null,
            logerUserId: null,
            phoneNumber: null,
            pobox: null,
            street: null,
            title: null,
            userName: null,
            zip: null,
            entityAuthorizations: [],
        };
        return newEntityContact;
    }

    const rowClick = (e: any, row: any) => {
        clearInputState()
        var selectedContact = row.getData();
        var edate = selectedContact.endDate;

        setEntityContact(selectedContact);
        setTitle(selectedContact.title);
        setEmail(selectedContact.email);
        setConfirmEmail(selectedContact.email);
        setReportAccessDataGivenAuthorization(selectedContact.entityAuthorizations);
        setFirstName(selectedContact.firstName);
        setLastName(selectedContact.lastName);
        setStartDate(dateHelper.convertToDate(selectedContact.startDate));
        setEndDate(dateHelper.convertToDate(selectedContact.endDate));
        setPhoneNumber(selectedContact.phoneNumber);
        if (selectedContact.extension) {
            setPhoneExtension(selectedContact.extension)
        } else {
            setPhoneExtension("");
        }

        if (edate == null) {
            setCanEdit(true);
            if (props.isStaff) {
                setEditMode('EDIT_STAFF');
            } else {
                setEditMode('EDIT_USER');





            }
        } else {
            setCanEdit(false);
            if (props.isStaff) {
                setEditMode('LANDING_STAFF');
            } else {
                setEditMode('LANDING');
            }
        }
    };

    

    const IsInputTextValid = (textValue: string): boolean | undefined => {
        var isValid : boolean | undefined = undefined;
        if (canEdit) {
            if (entityContact) {
                if (textValue) {
                    if (textValue.trim().length > 0) {
                        isValid = true;
                    }
                    else {
                        isValid = false;
                    }
                }
                else {
                    isValid = false;
                }
            }
        }
        return isValid
    }

    const IsInputTextInvalid = (textValue: string | undefined): boolean | undefined => {
        var isInvalid : boolean | undefined = undefined;

        if (canEdit) {
            if (entityContact) {
                if (textValue) {
                    if (textValue.trim().length <= 0) {
                        isInvalid = true;
                    }
                    else {
                        isInvalid = false;
                    }
                }
                else {
                    isInvalid = true;
                }
            }
        }

        return isInvalid
    }


    const reportAccessesAreValid = (): boolean => {
        var result = true;

        if ((afrReportAccess == false && courtReporting939ReportAccess == false && courtReporting29ReportAccess == false)
            || (afrReportAccess == true && (selectedContactRoleAfr.value == defaultValue.value || selectedUserRoleAfr.value == defaultValue.value))
            || (courtReporting939ReportAccess == true && (selectedContactRole939.value == defaultValue.value || selectedUserRole939.value == defaultValue.value))
            || (courtReporting29ReportAccess == true && (selectedContactRole29.value == defaultValue.value || selectedUserRole29.value == defaultValue.value))) {
            result = false;
        }

        return result;
    }

    const IsValidForm = () => {
        var result = false;
        if (IsInputTextValid(firstName) &&
            IsInputTextValid(lastName) &&
            IsInputTextValid(email) &&
            ((email == confirmEmail) || (confirmEmailFadeIn == false && email)) &&
            reportAccessesAreValid() &&
            IsInputTextValid(title) &&
            (IsInputTextValid(phoneNumber) && phoneNumber.includes("_") == false)

        ) {
            //TODO 
            //add selecting record by what uniquely identifies a record
            result = true;
        }
        return result;
    }
    const IsValidStaffForm = () => {
        var result = false;
        if (IsInputTextValid(firstName) &&
            IsInputTextValid(lastName) 
        ) {
            //TODO 
            //add selecting record by what uniquely identifies a record
            result = true;
        }
        return result;
    }

    const clearInputState = () => {
        setFirstName("");
        setLastName("");
        setPhoneNumber("");
        setPhoneExtension("");
        setEmail("");
        setConfirmEmail("");
        setTitle("");
        setSelectedContactRoleAfr(defaultValue);
        setSelectedUserRoleAfr(defaultValue);
        setSelectedContactRole939(defaultValue);
        setSelectedUserRole939(defaultValue);
        setSelectedContactRole29(defaultValue);
        setSelectedUserRole29(defaultValue);
        setAfrReportAccess(false);
        setCourtReporting939ReportAccess(false);
        setCourtReporting29ReportAccess(false);
        setEntityContact(null);
        setConfirmEmailFadeIn(false);
        setStartDate(null);
        setEndDate(null);
    }


    const setReportAccessDataGivenAuthorization = (entityAuthorizationsList: Array<EntityAuthorization>) => {
        for (var i = 0; i < entityAuthorizationsList.length; i++) {
            switch (entityAuthorizationsList[i].reportAccess) {
                case ReportAccessEnum.AFR.toString():
                    var foundReportAccess = entityAuthorizationsList.find((ele: any) => ele.reportAccess == ReportAccessEnum.AFR.toString());

                    if (foundReportAccess) {
                        setAfrReportAccess(true);
                        let selectedContactRoleOption: { label: string; value: string } | undefined = contactRoleOptions.find((ele: any) => foundReportAccess != undefined && ele.value == foundReportAccess.entityContactRole);
                        if (selectedContactRoleOption) {
                            setSelectedContactRoleAfr(selectedContactRoleOption);
                        }

                        let selectedUserRoleOption: { label: string; value: string } | undefined = userRoleOptions.find((ele: any) => foundReportAccess != undefined && ele.value == foundReportAccess.entityUserRole);
                        if (selectedUserRoleOption) {
                            setSelectedUserRoleAfr(selectedUserRoleOption);
                        }
                    } else {
                        setAfrReportAccess(false);
                    }
                    break;

                case ReportAccessEnum.NINE_THIRTY_NINE.toString():
                    var foundReportAccess = entityAuthorizationsList.find((ele: any) => ele.reportAccess == ReportAccessEnum.NINE_THIRTY_NINE.toString());

                    if (foundReportAccess) {
                        setCourtReporting939ReportAccess(true);
                        let selectedContactRoleOption: { label: string; value: string } | undefined = contactRoleOptions.find((ele: any) => foundReportAccess != undefined && ele.value == foundReportAccess.entityContactRole);
                        if (selectedContactRoleOption) {
                            setSelectedContactRole939(selectedContactRoleOption);
                        }

                        let selectedUserRoleOption: { label: string; value: string } | undefined = userRoleOptions.find((ele: any) => foundReportAccess != undefined && ele.value == foundReportAccess.entityUserRole);
                        if (selectedUserRoleOption) {
                            setSelectedUserRole939(selectedUserRoleOption);
                        }
                    } else {
                        setCourtReporting939ReportAccess(false);
                    }
                    break;
                case ReportAccessEnum.TWENTY_NINE.toString():
                    var foundReportAccess = entityAuthorizationsList.find((ele: any) => ele.reportAccess == ReportAccessEnum.TWENTY_NINE.toString());

                    if (foundReportAccess) {
                        setCourtReporting29ReportAccess(true);
                        let selectedContactRoleOption: { label: string; value: string } | undefined = contactRoleOptions.find((ele: any) => foundReportAccess != undefined && ele.value == foundReportAccess.entityContactRole);
                        if (selectedContactRoleOption) {
                            setSelectedContactRole29(selectedContactRoleOption);
                        }

                        let selectedUserRoleOption: { label: string; value: string } | undefined = userRoleOptions.find((ele: any) => foundReportAccess != undefined && ele.value == foundReportAccess.entityUserRole);
                        if (selectedUserRoleOption) {
                            setSelectedUserRole29(selectedUserRoleOption);
                        }
                    } else {
                        setCourtReporting29ReportAccess(false);
                    }
                    break;
                default:
                    break;
            }
        }
    };

    const initializeStaffCreation = function () {
        clearInputState();
        setCanEdit(true);
        setEditMode('CREATE_STAFF');

        let newEntityContact: DatabaseEntityContact = getBlankEntity();
        var sDate: Date = new Date();
        newEntityContact.startDate = sDate.toString();
        newEntityContact.entityCode = entitySelector ? entitySelector.code : "";
        setStartDate(sDate);
        setEntityContact(newEntityContact);
//        setStartDate(newEntityContact.startDate);

    }
    const getDepartmentStaffAndInitializeHooks = async (email: string, entityCode: string) => {
        if (canEdit) {
            setEditMode('EDIT_STAFF');
        } else {
            setEditMode('LANDING_STAFF');
        }
    }
    const getEntityContactAndInitializeHooks = async (email: string, entityCode: string) => {
        setIsLoadingUserData(true);
        var resultAction = await dispatch(getEntityContactByEmail({ entityCode, email }));
        var originalPromiseResult = unwrapResult(resultAction);

        //existing user, load data
        if (originalPromiseResult && originalPromiseResult.logerUserId) {
            setEntityContact(originalPromiseResult);
            setFirstName(originalPromiseResult.firstName);
            setLastName(originalPromiseResult.lastName);
            setPhoneNumber(originalPromiseResult.phoneNumber);

            setEmail(originalPromiseResult.email);
            setConfirmEmail(originalPromiseResult.email);
            setTitle(originalPromiseResult.title);

            if (originalPromiseResult.entityAuthorizations && originalPromiseResult.entityAuthorizations.length > 0) {
                setReportAccessDataGivenAuthorization(originalPromiseResult.entityAuthorizations);
            }

            if (originalPromiseResult.extension) {
                setPhoneExtension(originalPromiseResult.extension);
            } else {
                setPhoneExtension("");
            }

            setStreet(originalPromiseResult.street);
            setZipCode(originalPromiseResult.zip);
            setPobox(originalPromiseResult.pobox);
            setCityCode(originalPromiseResult.cityCode);

            setEditMode('EDIT_USER');
        }
        else {
            clearInputState();
            setEmail(email);
            let newEntityContact: DatabaseEntityContact = getBlankEntity();
            newEntityContact.startDate = DateTime.fromISO();
            newEntityContact.email = email;

            newEntityContact.entityCode = entitySelector ? entitySelector.code : "";

            resultAction = await dispatch(getUserDataByEmail(email));
            originalPromiseResult = unwrapResult(resultAction);
            if (originalPromiseResult) {
                setFirstName(originalPromiseResult.firstName);
                setLastName(originalPromiseResult.lastName);


                newEntityContact.userName = email;
                newEntityContact.firstName = originalPromiseResult.firstName;
                newEntityContact.lastName = originalPromiseResult.lastName;

            }
            else {
                setConfirmEmailFadeIn(true);
            }

            setEntityContact(newEntityContact);
            setEditMode('CREATE_USER');
         
        }
        setIsLoadingUserData(false);
    }


    const handleReportAccessChange = (checked: boolean, reportAccessName: string) => {
        if (entityContact) {

            switch (reportAccessName) {
                case ReportAccessEnum.AFR:
                    setAfrReportAccess(checked);
                    if (checked == false) {
                        setSelectedContactRoleAfr(defaultValue);
                        setSelectedUserRoleAfr(defaultValue);
                    }
                    break;
                case ReportAccessEnum.NINE_THIRTY_NINE:
                    setCourtReporting939ReportAccess(checked);
                    if (checked == false) {
                        setSelectedContactRole939(defaultValue);
                        setSelectedUserRole939(defaultValue);
                    }
                    break;
                case ReportAccessEnum.TWENTY_NINE:
                    setCourtReporting29ReportAccess(checked);
                    if (checked == false) {
                        setSelectedContactRole29(defaultValue);
                        setSelectedUserRole29(defaultValue);
                    }
                    break;
                default:

                    break;
            }
        }
    }

    React.useEffect(() => {
        if (editMode != 'LANDING' && editMode != 'LANDING_STAFF') {
            if (emailValue !== "" && emailValue !== null && emailValue !== undefined) {
                if (props.isStaff) {
                    getDepartmentStaffAndInitializeHooks(emailValue, currentEntityCodeSelector);
                } else {
                    getEntityContactAndInitializeHooks(emailValue, currentEntityCodeSelector);
                }
            }
        }
    }, [emailValue])

    React.useEffect(() => {
        setContactListData(processEntityContacts(databaseContactsSelector));
    }, [databaseContactsSelector])

    React.useEffect(() => {

        if (entitySyncerIsOpen == false) {
            setContactChangeValue(undefined);
        }

    }, [entitySyncerIsOpen])

    const isContactRoleAllowable = (optionValue: string, reportAccessType: ReportAccessEnum) => {
        var isAllowable = false;

        let reportAccessValue: string = "";
        let reportAccessName: string = "";

        switch (reportAccessType) {
            case ReportAccessEnum.AFR:
                reportAccessValue = selectedUserRoleAfr.value;
                reportAccessName = 'AFR'
                break;

            case ReportAccessEnum.NINE_THIRTY_NINE:
                reportAccessValue = selectedUserRole939.value;
                reportAccessName = 'NINE_THIRTY_NINE'
                break;
            case ReportAccessEnum.TWENTY_NINE:
                reportAccessValue = selectedUserRole29.value;
                reportAccessName = 'TWENTY_NINE'
                break;
            default:
                break;
        }


        if (optionValue == "") {
            isAllowable = true;
        }
        else {
            switch (reportAccessValue) {
                case "EDITOR":
                    if (optionValue == "PRIMARY" ||
                        optionValue == "SECONDARY") {
                        if (optionValue == "SECONDARY") {
                            isAllowable = true;
                        } else {
                            switch (reportAccessType) {

                                case ReportAccessEnum.AFR:
                                    if (afrHasPrimaryEditor == false) {
                                        isAllowable = true;
                                    }
                                    break;

                                case ReportAccessEnum.NINE_THIRTY_NINE:
                                    if (courtReporting939HasPrimaryEditor == false) {
                                        isAllowable = true;
                                    }
                                    break;

                                case ReportAccessEnum.TWENTY_NINE:
                                    if (courtReporting29HasPrimaryEditor == false) {
                                        isAllowable = true;
                                    }
                                    break;

                                default:
                                    break;
                            }
                        }

                    }
                    break;

                case "THIRD_PARTY":
                    if (optionValue == "AUDITOR" ||
                        optionValue == "MANAGEMENT_COMPANY" ||
                        optionValue == "OTHER") {
                        isAllowable = true;
                    }
                    break;

                case "NOTIFICATION":
                    if (optionValue == "CONTACT") {
                        isAllowable = true;
                    }
                    break;

                default:
                    break;
            }
        }
        return isAllowable;
    }

    const landingModeTemplate = (
        <React.Fragment>
            <Form>
                <Row>
                    <Col md="6">
                        <h4>Local Government User</h4>
                        {!props.isStaff &&
                            <FormGroup>
                                <Row>
                                    <Col>
                                        <Label>Email:</Label>
                                    </Col>
                                    <Col md="9">
                                        <Input type="email" value={email} disabled={true} />
                                    </Col>
                                </Row>
                            </FormGroup>
                        }
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>First Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={firstName} disabled={true} />
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Last Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={lastName} disabled={true} />
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Title:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={title} disabled={true} />
                                </Col>
                            </Row>
                        </FormGroup>
                        {!props.isStaff &&
                            <FormGroup>
                                <Row>
                                    <Col >
                                        <Label>Phone Number:</Label>
                                    </Col>
                                    <Col md="4">
                                        <Input
                                            type="tel"
                                            mask="(999) 999-9999"
                                            value={phoneNumber}
                                            tag={InputMask}
                                            disabled={true}
                                        />
                                    </Col>
                                    <Col>
                                        <Label>Extension:</Label>
                                    </Col>
                                    <Col md="2">
                                        <Input
                                            value={phoneExtension}
                                            disabled={true}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>
                        }
                        <Row>
                            {
                                entitySelector ?
                                    (<React.Fragment>
                                        <CanShowGivenAuthorizedContactUserRoles
                                            entityContactRole={EntityContactRole.PRIMARY}
                                            entityUserRole={EntityUserRole.EDITOR}
                                            reportAccess={null}
                                        >
                                            <Col>
                                                <Button hidden={editMode != 'LANDING'} onClick={(e) => {
                                                    clearInputState();
                                                    setCanEdit(true);
                                                    setEditMode('CREATE_USER');
                                                }}>Create User</Button>
                                            </Col>
                                        </CanShowGivenAuthorizedContactUserRoles>
                                    </React.Fragment>)
                                    : (<CanShowGivenAuthorizedContactUserRoles
                                        entityContactRole={EntityContactRole.PRIMARY}
                                        entityUserRole={EntityUserRole.EDITOR}
                                        reportAccess={null}
                                    >
                                        <Col>
                                            <FormText>Choose an entity from the entity selector.</FormText>
                                        </Col>
                                    </CanShowGivenAuthorizedContactUserRoles>)
                            }
                        </Row>
                    </Col>
                    <Col md="6">
                        {entityContact ? <React.Fragment>
                            <h4>User Assignment Information</h4>
                            <Nav tabs>
                                <NavItem hidden={afrEnabled == false}>
                                    <NavLink
                                        active={ activeTab === '1'}
                                        onClick={() => { toggle('1'); }}>
                                        AFR
                                    </NavLink>
                                </NavItem>
                                <NavItem hidden={courtReporting939Enabled == false}>
                                    <NavLink
                                        active={activeTab === '2'}
                                        onClick={() => { toggle('2'); }}>
                                        939.185
                                    </NavLink>
                                </NavItem>
                                <NavItem hidden={courtReporting29Enabled == false}>
                                    <NavLink
                                        active={activeTab === '3'}
                                        onClick={() => { toggle('3'); }}>
                                        29.0085
                                    </NavLink>
                                </NavItem>
                            </Nav>
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1" hidden={afrEnabled == false}>
                                    <Row style={{ marginTop: "1em" }}>
                                        <Col>
                                            <FormGroup>
                                                <Row>
                                                    <Col md="4" >
                                                        <Label>Report Access:</Label>
                                                    </Col>
                                                    <Col >
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                checked={afrReportAccess}
                                                                disabled={true}
                                                                onChange={(e) => handleReportAccessChange(e.target.checked, ReportAccessEnum.AFR)} />{' '}
                                                            AFR
                                                        </Label>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <FormGroup>
                                        <Row>
                                            <Col>
                                                <Label for="accessRoleSelect">Access Role:</Label>
                                            </Col>
                                            <Col md="9">
                                                <Select
                                                    options={userRoles}
                                                    value={selectedUserRoleAfr}
                                                    isDisabled={true}
                                                />
                                            </Col>
                                        </Row>
                                    </FormGroup>

                                    <FormGroup>
                                        <Row>
                                            <Col >
                                                <Label for="contactRoleSelect">User Role:</Label>
                                            </Col>
                                            <Col md="9">
                                                <Select
                                                    options={contactRoles.filter(option => isContactRoleAllowable(option.value, ReportAccessEnum.AFR))}
                                                    value={selectedContactRoleAfr}
                                                    isDisabled={true}
                                                    isOptionDisabled={true}
                                                />
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                </TabPane>
                                <TabPane tabId="2" hidden={courtReporting939Enabled == false}>
                                    <Row style={{ marginTop: "1em" }}>
                                        <Col>
                                            <FormGroup>
                                                <Row>
                                                    <Col md="4" >
                                                        <Label>Report Access:</Label>
                                                    </Col>
                                                    <Col >
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                disabled={true}
                                                                checked={courtReporting939ReportAccess}
                                                                onChange={(e) => handleReportAccessChange(e.target.checked, ReportAccessEnum.NINE_THIRTY_NINE)} />{' '}
                                                            939.185
                                                        </Label>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <FormGroup>
                                        <Row>
                                            <Col>
                                                <Label for="accessRoleSelect">Access Role:</Label>
                                            </Col>
                                            <Col md="9">
                                                <Select
                                                    options={userRoles}
                                                    value={selectedUserRole939}
                                                    onChange={(e: { value: string, label: string }) => { setSelectedUserRole939(e); setSelectedContactRole939(defaultValue); }}
                                                    isDisabled={true}
                                                />
                                            </Col>
                                        </Row>
                                    </FormGroup>

                                    <FormGroup>
                                        <Row>
                                            <Col >
                                                <Label for="contactRoleSelect">User Role:</Label>
                                            </Col>
                                            <Col md="9">
                                                <Select
                                                    options={contactRoles.filter(option => isContactRoleAllowable(option.value, ReportAccessEnum.NINE_THIRTY_NINE))}
                                                    value={selectedContactRole939}
                                                    onChange={(e: { value: string, label: string }) => setSelectedContactRole939(e)}
                                                    isDisabled={true}
                                                />
                                            </Col>
                                        </Row>
                                    </FormGroup>

                                </TabPane>
                                <TabPane tabId="3" hidden={courtReporting29Enabled == false}>
                                    <Row style={{ marginTop: "1em" }}>
                                        <Col>
                                            <FormGroup>
                                                <Row>
                                                    <Col md="4" >
                                                        <Label>Report Access:</Label>
                                                    </Col>
                                                    <Col >
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                checked={courtReporting29ReportAccess}
                                                                disabled={true}
                                                                onChange={(e) => handleReportAccessChange(e.target.checked, ReportAccessEnum.TWENTY_NINE)} />{' '}
                                                            29.0085
                                                        </Label>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <FormGroup>
                                        <Row>
                                            <Col>
                                                <Label for="accessRoleSelect29">Access Role:</Label>
                                            </Col>
                                            <Col md="9">
                                                <Select
                                                    options={userRoles}
                                                    value={selectedUserRole29}
                                                    onChange={(e: { value: string, label: string }) => { setSelectedUserRole29(e); setSelectedContactRole29(defaultValue); }}
                                                    isDisabled={true}
                                                />
                                            </Col>
                                        </Row>
                                    </FormGroup>

                                    <FormGroup>
                                        <Row>
                                            <Col >
                                                <Label for="contactRoleSelect29">User Role:</Label>
                                            </Col>
                                            <Col md="9">
                                                <Select
                                                    options={contactRoles.filter(option => isContactRoleAllowable(option.value, ReportAccessEnum.TWENTY_NINE))}
                                                    value={selectedContactRole29}
                                                    onChange={(e: { value: string, label: string }) => setSelectedContactRole29(e)}
                                                    isDisabled={true}
                                                />
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                </TabPane>
                            </TabContent>
                        </React.Fragment>
                            : <Label>{usageMsg}</Label>
                        }


                    </Col>
                </Row>
            </Form>
        </React.Fragment>
    );

    const createUserModeTemplate = (
        <React.Fragment>
            <Form>
                <Row>
                    <Col md="6">
                        <h4>Local Government User</h4>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Email:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="email" value={email} disabled={!canEdit}
                                        onChange={(e) => { setEmail(e.target.value) }}
                                        valid={IsInputTextValid(email)}
                                        invalid={IsInputTextInvalid(email)} />
                                    <FormFeedback>Email name is required.</FormFeedback>
                                    <FormText>Group emails should not be used.</FormText>
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Confirm Email:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="email" value={confirmEmail} disabled={!canEdit}
                                        onChange={(e) => setConfirmEmail(e.currentTarget.value)}
                                        valid={IsInputTextValid(confirmEmail) && (email == confirmEmail)}
                                        invalid={IsInputTextInvalid(confirmEmail) || (email != confirmEmail)} />
                                    <FormFeedback>
                                        Confirmation Email is required and must match the Email.
                                    </FormFeedback>
                                    <br />
                                </Col>
                            </Row>
                        </FormGroup>

                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>First Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={firstName} disabled={!canEdit}
                                        onChange={(e) => setFirstName(e.currentTarget.value)}
                                        valid={IsInputTextValid(firstName)}
                                        invalid={IsInputTextInvalid(firstName)} />
                                    <FormFeedback>First name is required.</FormFeedback>
                                </Col>
                            </Row>

                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Last Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={lastName} disabled={!canEdit}
                                        onChange={(e) => setLastName(e.currentTarget.value)}
                                        valid={IsInputTextValid(lastName)}
                                        invalid={IsInputTextInvalid(lastName)} />
                                    <FormFeedback>Last name is required.</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Title:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={title} disabled={!canEdit}
                                        onChange={(e) => setTitle(e.currentTarget.value)}
                                        valid={IsInputTextValid(title)}
                                        invalid={IsInputTextInvalid(title)} />
                                    <FormFeedback>Title is required.</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Phone Number:</Label>
                                </Col>
                                <Col md="4">
                                    <Input
                                        type="tel"
                                        mask="(999) 999-9999"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.currentTarget.value)}
                                        tag={InputMask}
                                        disabled={!canEdit}
                                        valid={(IsInputTextValid(phoneNumber) && phoneNumber.includes("_") == false)}
                                        invalid={(IsInputTextInvalid(phoneNumber) || phoneNumber.includes("_") == true)} />
                                    <FormFeedback>Phone number is required.</FormFeedback>
                                </Col>
                                <Col>
                                    <Label>Extension:</Label>
                                </Col>
                                <Col md="2">
                                    <Input
                                        value={phoneExtension}
                                        onChange={(e) => setPhoneExtension(e.currentTarget.value)}
                                        disabled={!canEdit}
                                    />
                                </Col>

                            </Row>
                        </FormGroup>
                        <Row>
                            <Col>
                                <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                                    type="grow" color="warning"
                                    hidden={isLoadingUserData == false} />

                                <div hidden={isLoadingUserData == true}>
                                    <Button
                                        id="createUser"
                                        onClick={handleAddContactClick}
                                        className="taxedit"
                                        disabled={!IsValidForm()}
                                    >Create User</Button>

                                    <Button
                                        style={{ marginLeft: "2em" }}
                                        color="danger"
                                        onClick={handleCancelClick}
                                    >Cancel</Button>
                                </div>
                            </Col>

                        </Row>
                    </Col>
                    <Col md="6">
                        {entityContact ? <React.Fragment>
                            <h4 style={{}}>User Assignment Information</h4>
                            <Nav tabs>
                                <NavItem hidden={afrEnabled == false}>
                                    <NavLink
                                        active={activeTab === '1'}
                                        onClick={() => { toggle('1'); }}>
                                        AFR
                                    </NavLink>
                                </NavItem>
                                <NavItem hidden={courtReporting939Enabled == false}>
                                    <NavLink
                                        active={activeTab === '2'}
                                        onClick={() => { toggle('2'); }}>
                                        939.185
                                    </NavLink>
                                </NavItem>
                                <NavItem hidden={courtReporting29Enabled == false}>
                                    <NavLink
                                        active={activeTab === '3'}
                                        onClick={() => { toggle('3'); }}>
                                        29.0085
                                    </NavLink>
                                </NavItem>
                            </Nav>
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1" hidden={afrEnabled == false}>
                                    <Row style={{ marginTop: "1em" }}>
                                        <Col>
                                            <FormGroup>
                                                <Row>
                                                    <Col md="4" >
                                                        <Label>Report Access:</Label>
                                                    </Col>
                                                    <Col >
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                checked={afrReportAccess}
                                                                onChange={(e) => handleReportAccessChange(e.target.checked, ReportAccessEnum.AFR)} />{' '}
                                                            AFR
                                                        </Label>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row style={{ marginTop: "1em" }}>
                                        <Col sm="12">
                                            <FormGroup>
                                                <Row>
                                                    <Col>
                                                        <Label for="accessRoleSelect">Access Role:</Label>
                                                    </Col>
                                                    <Col md="9">
                                                        <Select
                                                            options={userRoles}
                                                            value={selectedUserRoleAfr}
                                                            onChange={(e: { value: string, label: string }) => { setSelectedUserRoleAfr(e); setSelectedContactRoleAfr(defaultValue); }}
                                                            isDisabled={!canEdit || (afrReportAccess == false)}
                                                        />

                                                        <Input type="hidden" value={selectedUserRoleAfr.value}
                                                            onChange={(e) => { }}
                                                            valid={selectedUserRoleAfr.value != defaultValue.value && editMode == 'CREATE_USER' && afrReportAccess}
                                                            invalid={selectedUserRoleAfr.value == defaultValue.value && editMode == 'CREATE_USER' && afrReportAccess}
                                                            disabled={true}
                                                        />
                                                        <FormFeedback>Entity access role is required.</FormFeedback>
                                                    </Col>
                                                </Row>
                                            </FormGroup>

                                            <FormGroup>
                                                <Row>
                                                    <Col >
                                                        <Label for="contactRoleSelect">User Role:</Label>
                                                    </Col>
                                                    <Col md="9">
                                                        <Select
                                                            options={contactRoles.filter(option => isContactRoleAllowable(option.value, ReportAccessEnum.AFR))}
                                                            value={selectedContactRoleAfr}
                                                            onChange={(e: { value: string, label: string }) => setSelectedContactRoleAfr(e)}
                                                            isDisabled={!canEdit || (afrReportAccess == false)}
                                                            
                                                        />
                                                        <Input type="hidden" value={selectedContactRoleAfr.value}
                                                            onChange={(e) => { }}
                                                            valid={selectedContactRoleAfr.value != defaultValue.value && editMode == 'CREATE_USER' && afrReportAccess}
                                                            invalid={selectedContactRoleAfr.value == defaultValue.value && editMode == 'CREATE_USER' && afrReportAccess}
                                                            disabled={true}
                                                        />
                                                        <FormFeedback>Entity user role is required.</FormFeedback>
                                                        <FormText>Only one active primary.</FormText>
                                                    </Col>
                                                </Row>
                                            </FormGroup>

                                        </Col>
                                    </Row>

                                </TabPane>
                                <TabPane tabId="2" hidden={courtReporting939Enabled == false}>
                                    <Row style={{ marginTop: "1em" }}>
                                        <Col>
                                            <FormGroup>
                                                <Row>
                                                    <Col md="4" >
                                                        <Label>Report Access:</Label>
                                                    </Col>
                                                    <Col >
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                checked={courtReporting939ReportAccess}
                                                                onChange={(e) => handleReportAccessChange(e.target.checked, ReportAccessEnum.NINE_THIRTY_NINE)} />{' '}
                                                            939.185
                                                        </Label>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row style={{ marginTop: "1em" }}>
                                        <Col sm="12">
                                            <FormGroup>
                                                <Row>
                                                    <Col>
                                                        <Label for="accessRoleSelect">Access Role:</Label>
                                                    </Col>
                                                    <Col md="9">
                                                        <Select
                                                            options={userRoles}
                                                            value={selectedUserRole939}
                                                            onChange={(e: { value: string, label: string }) => { setSelectedUserRole939(e); setSelectedContactRole939(defaultValue); }}
                                                            isDisabled={!canEdit || (courtReporting939ReportAccess == false)}
                                                        />

                                                        <Input type="hidden" value={selectedUserRole939.value}
                                                            onChange={(e) => { }}
                                                            valid={selectedUserRole939.value != defaultValue.value && editMode == 'CREATE_USER' && courtReporting939ReportAccess}
                                                            invalid={selectedUserRole939.value == defaultValue.value && editMode == 'CREATE_USER' && courtReporting939ReportAccess}
                                                            disabled={true}
                                                        />
                                                        <FormFeedback>Entity access role is required.</FormFeedback>
                                                    </Col>
                                                </Row>
                                            </FormGroup>

                                            <FormGroup>
                                                <Row>
                                                    <Col >
                                                        <Label for="contactRoleSelect">User Role:</Label>
                                                    </Col>
                                                    <Col md="9">
                                                        <Select
                                                            options={contactRoles.filter(option => isContactRoleAllowable(option.value, ReportAccessEnum.NINE_THIRTY_NINE))}
                                                            value={selectedContactRole939}
                                                            onChange={(e: { value: string, label: string }) => setSelectedContactRole939(e)}
                                                            isDisabled={!canEdit || (courtReporting939ReportAccess == false)}
                                                            

                                                        />
                                                        <Input type="hidden" value={selectedContactRole939.value}
                                                            onChange={(e) => { }}
                                                            valid={selectedContactRole939.value != defaultValue.value && editMode == 'CREATE_USER' && courtReporting939ReportAccess}
                                                            invalid={selectedContactRole939.value == defaultValue.value && editMode == 'CREATE_USER' && courtReporting939ReportAccess}
                                                            disabled={true}
                                                        />
                                                        <FormFeedback>Entity user role is required.</FormFeedback>
                                                        <FormText>Only one active primary.</FormText>
                                                    </Col>
                                                </Row>
                                            </FormGroup>

                                        </Col>
                                    </Row>
                                </TabPane>
                                <TabPane tabId="3" hidden={courtReporting29Enabled == false}>
                                    <Row style={{ marginTop: "1em" }}>
                                        <Col>
                                            <FormGroup>
                                                <Row>
                                                    <Col md="4" >
                                                        <Label>Report Access:</Label>
                                                    </Col>
                                                    <Col >
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                checked={courtReporting29ReportAccess}
                                                                onChange={(e) => handleReportAccessChange(e.target.checked, ReportAccessEnum.TWENTY_NINE)} />{' '}
                                                            29.0085
                                                        </Label>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row style={{ marginTop: "1em" }}>
                                        <Col sm="12">
                                            <FormGroup>
                                                <Row>
                                                    <Col>
                                                        <Label for="accessRoleSelect29">Access Role:</Label>
                                                    </Col>
                                                    <Col md="9">
                                                        <Select
                                                            options={userRoles}
                                                            value={selectedUserRole29}
                                                            onChange={(e: { value: string, label: string }) => { setSelectedUserRole29(e); setSelectedContactRole29(defaultValue); }}
                                                            isDisabled={!canEdit || (courtReporting29ReportAccess == false)}
                                                        />

                                                        <Input type="hidden" value={selectedUserRole29.value}
                                                            onChange={(e) => { }}
                                                            valid={selectedUserRole29.value != defaultValue.value && editMode == 'CREATE_USER' && courtReporting29ReportAccess}
                                                            invalid={selectedUserRole29.value == defaultValue.value && editMode == 'CREATE_USER' && courtReporting29ReportAccess}
                                                            disabled={true}
                                                        />
                                                        <FormFeedback>Entity access role is required.</FormFeedback>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                            <FormGroup>
                                                <Row>
                                                    <Col >
                                                        <Label for="contactRoleSelect29">User Role:</Label>
                                                    </Col>
                                                    <Col md="9">
                                                        <Select
                                                            options={contactRoles.filter(option => isContactRoleAllowable(option.value, ReportAccessEnum.TWENTY_NINE))}
                                                            value={selectedContactRole29}
                                                            onChange={(e: { value: string, label: string }) => setSelectedContactRole29(e)}
                                                            isDisabled={!canEdit || (courtReporting29ReportAccess == false)}


                                                        />
                                                        <Input type="hidden" value={selectedContactRole29.value}
                                                            onChange={(e) => { }}
                                                            valid={selectedContactRole29.value != defaultValue.value && editMode == 'CREATE_USER' && courtReporting29ReportAccess}
                                                            invalid={selectedContactRole29.value == defaultValue.value && editMode == 'CREATE_USER' && courtReporting29ReportAccess}
                                                            disabled={true}
                                                        />
                                                        <FormFeedback>Entity user role is required.</FormFeedback>
                                                        <FormText>Only one active primary.</FormText>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </TabPane>
                            </TabContent>
                        </React.Fragment>

                            : <Label>Please enter the email to set report access data.</Label>}

                    </Col>
                </Row>
            </Form>

        </React.Fragment>
    );

    const editUserModeTemplate = (
        <React.Fragment>
            <Form>
                <Row>
                    <Col md="6">
                        <h4>Local Government User</h4>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Email:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="email" value={email} disabled={true} />
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>First Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={firstName} disabled={!canEdit}
                                        onChange={(e) => setFirstName(e.currentTarget.value)}
                                        valid={IsInputTextValid(firstName)}
                                        invalid={IsInputTextInvalid(firstName)} />
                                    <FormFeedback>First name is required.</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Last Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={lastName} disabled={!canEdit}
                                        onChange={(e) => setLastName(e.currentTarget.value)}
                                        valid={IsInputTextValid(lastName)}
                                        invalid={IsInputTextInvalid(lastName)} />
                                    <FormFeedback>Last name is required.</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Title:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={title} disabled={!canEdit}
                                        onChange={(e) => setTitle(e.currentTarget.value)}
                                        valid={IsInputTextValid(title)}
                                        invalid={IsInputTextInvalid(title)} />
                                    <FormFeedback>Title is required.</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Phone Number:</Label>
                                </Col>
                                <Col md="4">
                                    <Input
                                        type="tel"
                                        mask="(999) 999-9999"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.currentTarget.value)}
                                        tag={InputMask}
                                        disabled={!canEdit}
                                        valid={(IsInputTextValid(phoneNumber) && phoneNumber.includes("_") == false)}
                                        invalid={(IsInputTextInvalid(phoneNumber) || phoneNumber.includes("_") == true)} />
                                    <FormFeedback>Phone number is required.</FormFeedback>
                                </Col>
                                <Col>
                                    <Label>Extension:</Label>
                                </Col>
                                <Col md="2">
                                    <Input
                                        value={phoneExtension}
                                        onChange={(e) => setPhoneExtension(e.currentTarget.value)}
                                        disabled={!canEdit}
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                        <Row>
                            <Col>
                                <Row>
                                    <Col>
                                        <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                                            type="grow" color="warning"
                                            hidden={isLoadingUserData == false} />

                                        <div hidden={isLoadingUserData == true}>
                                            <Button type="button" onClick={(e) => handleSaveButtonClick(true)}
                                                className="taxedit"
                                                disabled={!IsValidForm()}
                                            >
                                                Save
                                            </Button>

                                            <Button
                                                style={{ marginLeft: "2em" }}
                                                color="danger"
                                                onClick={handleCancelClick}>
                                                Cancel
                                            </Button>

                                            <Button
                                                hidden={(entityContact == null || (entityContact != null && entityContact.endDate != null) ? true : false)}
                                                style={{ marginLeft: "2em" }}
                                                onClick={(e) => handleDeauthorizeClick()}
                                            >
                                                Deauthorize
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                    <Col md="6">
                        <h4 style={{}}>User Assignment Information</h4>
                        <Nav tabs>

                            <NavItem hidden={afrEnabled == false}>
                                <NavLink
                                    active={activeTab === '1'}
                                    onClick={() => { toggle('1'); }}>
                                    AFR
                                </NavLink>
                            </NavItem>
                            <NavItem hidden={courtReporting939Enabled == false}>
                                <NavLink
                                    active={activeTab === '2'}
                                    onClick={() => { toggle('2'); }}>
                                    939.185
                                </NavLink>
                            </NavItem>
                            <NavItem hidden={courtReporting29Enabled == false}>
                                <NavLink
                                    active={activeTab === '3'}
                                    onClick={() => { toggle('3'); }}>
                                    29.0085
                                </NavLink>
                            </NavItem>
                        </Nav>
                        <TabContent activeTab={activeTab}>
                            <TabPane tabId="1" hidden={afrEnabled == false}>
                                <Row style={{ marginTop: "1em" }}>
                                    <Col>
                                        <FormGroup>
                                            <Row>
                                                <Col md="4" >
                                                    <Label>Report Access:</Label>
                                                </Col>
                                                <Col >
                                                    <Label check>
                                                        <Input
                                                            type="checkbox"
                                                            checked={afrReportAccess}
                                                            onChange={(e) => handleReportAccessChange(e.target.checked, ReportAccessEnum.AFR)} />{' '}
                                                        AFR
                                                    </Label>
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row style={{ marginTop: "1em" }}>
                                    <Col sm="12">
                                        <FormGroup>
                                            <Row>
                                                <Col>
                                                    <Label for="accessRoleSelect">Access Role:</Label>
                                                </Col>
                                                <Col md="9">
                                                    <Select
                                                        options={userRoles}
                                                        value={selectedUserRoleAfr}
                                                        onChange={(e: { value: string, label: string }) => { setSelectedUserRoleAfr(e); setSelectedContactRoleAfr(defaultValue); }}
                                                        isDisabled={!canEdit || (afrReportAccess == false)}
                                                    />

                                                    <Input type="hidden" value={selectedUserRoleAfr.value}
                                                        onChange={(e) => { }}
                                                        valid={selectedUserRoleAfr.value != defaultValue.value && editMode == 'EDIT_USER' && afrReportAccess}
                                                        invalid={selectedUserRoleAfr.value == defaultValue.value && editMode == 'EDIT_USER' && afrReportAccess}
                                                        disabled={true}
                                                    />
                                                    <FormFeedback>Entity access role is required.</FormFeedback>
                                                </Col>
                                            </Row>
                                        </FormGroup>

                                        <FormGroup>
                                            <Row>
                                                <Col >
                                                    <Label for="contactRoleSelect">User Role:</Label>
                                                </Col>
                                                <Col md="9">
                                                    <Select
                                                        options={contactRoles.filter(option => isContactRoleAllowable(option.value, ReportAccessEnum.AFR))}
                                                        value={selectedContactRoleAfr}
                                                        onChange={(e: { value: string, label: string }) => setSelectedContactRoleAfr(e)}
                                                        isDisabled={!canEdit || (afrReportAccess == false)}
                                                        

                                                    />
                                                    <Input type="hidden" value={selectedContactRoleAfr.value}
                                                        onChange={(e) => { }}
                                                        valid={selectedContactRoleAfr.value != defaultValue.value && editMode == 'EDIT_USER' && afrReportAccess}
                                                        invalid={selectedContactRoleAfr.value == defaultValue.value && editMode == 'EDIT_USER' && afrReportAccess}
                                                        disabled={true}
                                                    />
                                                    <FormFeedback>Entity user role is required.</FormFeedback>
                                                    <FormText>Only one active primary.</FormText>
                                                </Col>
                                            </Row>
                                        </FormGroup>

                                    </Col>
                                </Row>

                            </TabPane>
                            <TabPane tabId="2" hidden={courtReporting939Enabled == false}>
                                <Row style={{ marginTop: "1em" }}>
                                    <Col>
                                        <FormGroup>
                                            <Row>
                                                <Col md="4" >
                                                    <Label>Report Access:</Label>
                                                </Col>
                                                <Col >
                                                    <Label check>
                                                        <Input
                                                            type="checkbox"
                                                            checked={courtReporting939ReportAccess}
                                                            onChange={(e) => handleReportAccessChange(e.target.checked, ReportAccessEnum.NINE_THIRTY_NINE)} />{' '}
                                                        939.185
                                                    </Label>
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row style={{ marginTop: "1em" }}>
                                    <Col sm="12">
                                        <FormGroup>
                                            <Row>
                                                <Col>
                                                    <Label for="accessRoleSelect">Access Role:</Label>
                                                </Col>
                                                <Col md="9">
                                                    <Select
                                                        options={userRoles}
                                                        value={selectedUserRole939}
                                                        onChange={(e: { value: string, label: string }) => { setSelectedUserRole939(e); setSelectedContactRole939(defaultValue); }}
                                                        isDisabled={!canEdit || (courtReporting939ReportAccess == false)}
                                                    />

                                                    <Input type="hidden" value={selectedUserRole939.value}
                                                        onChange={(e) => { }}
                                                        valid={selectedUserRole939.value != defaultValue.value && editMode == 'EDIT_USER' && courtReporting939ReportAccess}
                                                        invalid={selectedUserRole939.value == defaultValue.value && editMode == 'EDIT_USER' && courtReporting939ReportAccess}
                                                        disabled={true}
                                                    />
                                                    <FormFeedback>Entity access role is required.</FormFeedback>
                                                </Col>
                                            </Row>
                                        </FormGroup>

                                        <FormGroup>
                                            <Row>
                                                <Col >
                                                    <Label for="contactRoleSelect">User Role:</Label>
                                                </Col>
                                                <Col md="9">
                                                    <Select
                                                        options={contactRoles.filter(option => isContactRoleAllowable(option.value, ReportAccessEnum.NINE_THIRTY_NINE))}
                                                        value={selectedContactRole939}
                                                        onChange={(e: { value: string, label: string }) => setSelectedContactRole939(e)}
                                                        isDisabled={!canEdit || (courtReporting939ReportAccess == false)}
                                                        

                                                    />
                                                    <Input type="hidden" value={selectedContactRole939.value}
                                                        onChange={(e) => { }}
                                                        valid={selectedContactRole939.value != defaultValue.value && editMode == 'EDIT_USER' && courtReporting939ReportAccess}
                                                        invalid={selectedContactRole939.value == defaultValue.value && editMode == 'EDIT_USER' && courtReporting939ReportAccess}
                                                        disabled={true}
                                                    />
                                                    <FormFeedback>Entity user role is required.</FormFeedback>
                                                    <FormText>Only one active primary.</FormText>
                                                </Col>
                                            </Row>
                                        </FormGroup>

                                    </Col>
                                </Row>
                            </TabPane>
                            <TabPane tabId="3" hidden={courtReporting29Enabled == false}>
                                <Row style={{ marginTop: "1em" }}>
                                    <Col>
                                        <FormGroup>
                                            <Row>
                                                <Col md="4" >
                                                    <Label>Report Access:</Label>
                                                </Col>
                                                <Col >
                                                    <Label check>
                                                        <Input
                                                            type="checkbox"
                                                            checked={courtReporting29ReportAccess}
                                                            onChange={(e) => handleReportAccessChange(e.target.checked, ReportAccessEnum.TWENTY_NINE)} />{' '}
                                                        29.0085
                                                    </Label>
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row style={{ marginTop: "1em" }}>
                                    <Col sm="12">
                                        <FormGroup>
                                            <Row>
                                                <Col>
                                                    <Label for="accessRoleSelect29">Access Role:</Label>
                                                </Col>
                                                <Col md="9">
                                                    <Select
                                                        options={userRoles}
                                                        value={selectedUserRole29}
                                                        onChange={(e: { value: string, label: string }) => { setSelectedUserRole29(e); setSelectedContactRole29(defaultValue); }}
                                                        isDisabled={!canEdit || (courtReporting29ReportAccess == false)}
                                                    />

                                                    <Input type="hidden" value={selectedUserRole29.value}
                                                        onChange={(e) => { }}
                                                        valid={selectedUserRole29.value != defaultValue.value && editMode == 'EDIT_USER' && courtReporting29ReportAccess}
                                                        invalid={selectedUserRole29.value == defaultValue.value && editMode == 'EDIT_USER' && courtReporting29ReportAccess}
                                                        disabled={true}
                                                    />
                                                    <FormFeedback>Entity access role is required.</FormFeedback>
                                                </Col>
                                            </Row>
                                        </FormGroup>

                                        <FormGroup>
                                            <Row>
                                                <Col >
                                                    <Label for="contactRoleSelect29">User Role:</Label>
                                                </Col>
                                                <Col md="9">
                                                    <Select
                                                        options={contactRoles.filter(option => isContactRoleAllowable(option.value, ReportAccessEnum.TWENTY_NINE))}
                                                        value={selectedContactRole29}
                                                        onChange={(e: { value: string, label: string }) => setSelectedContactRole29(e)}
                                                        isDisabled={!canEdit || (courtReporting29ReportAccess == false)}
                                                        

                                                    />
                                                    <Input type="hidden" value={selectedContactRole29.value}
                                                        onChange={(e) => { }}
                                                        valid={selectedContactRole29.value != defaultValue.value && editMode == 'EDIT_USER' && courtReporting29ReportAccess}
                                                        invalid={selectedContactRole29.value == defaultValue.value && editMode == 'EDIT_USER' && courtReporting29ReportAccess}
                                                        disabled={true}
                                                    />
                                                    <FormFeedback>Entity user role is required.</FormFeedback>
                                                    <FormText>Only one active primary.</FormText>
                                                </Col>
                                            </Row>
                                        </FormGroup>

                                    </Col>
                                </Row>
                            </TabPane>
                        </TabContent>


                    </Col>
                </Row>
            </Form>
            <Row>
                <Col>
                    <EntityAuthorizationSyncer
                        userEmailValue={email ? email : ""}
                        isOpen={entitySyncerIsOpen}
                        setIsOpen={setEntitySyncerIsOpen}
                        contactToSync={contactChangeValue}
                        entityCode={currentEntityCodeSelector}
                    />
                    <EntityDeauthorizationSyncer userEmailValue={email ? email : ""}
                        isOpen={entityDeauthorizationSyncerIsOpen}
                        setIsOpen={setEntityDeauthorizationSyncerIsOpen}
                        entityCode={currentEntityCodeSelector}  />
                </Col>
            </Row>
        </React.Fragment>
    );

    const landingModeStaffTemplate = (
        <React.Fragment>
            <Form>
                <Row>
                    <Col md="6">
                        <h4>Department Staff Member</h4>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>First Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={firstName} disabled={true} />
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Last Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={lastName} disabled={true} />
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Title:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={title} disabled={true} />
                                </Col>
                            </Row>
                        </FormGroup>
                        <Row>
                            {
                                entitySelector ?
                                    (<React.Fragment>
                                        <CanShowGivenAfrReportAccess>
                                            <Col>
                                                <Button hidden={editMode != 'LANDING_STAFF'} onClick={(e) => {
                                                    initializeStaffCreation()
                                                }}>Create Staff Member</Button>
                                            </Col>
                                        </CanShowGivenAfrReportAccess>
                                    </React.Fragment>)
                                    : (<CanShowGivenAfrReportAccess>
                                        <Col>
                                            <FormText>Choose an Department from the Department selector.</FormText>
                                        </Col>
                                    </CanShowGivenAfrReportAccess>)
                            }
                        </Row>
                    </Col>
                    <Col>
                        <Label>{usageMsg}</Label>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Starting Date:  {startDate ? startDate.toLocaleDateString() : ""}</Label>
                                </Col>
                            </Row>
                            <Row>
                                <Col >
                                    <Label>Ending Date:  {endDate ? endDate.toLocaleDateString() : ""}</Label>
                                </Col>
                            </Row>
                        </FormGroup>
                   </Col>
                </Row>
            </Form>
        </React.Fragment>
    );

    const createStaffModeTemplate = (
        <React.Fragment>
            <Form>
                <Row>
                    <h4>Department Staff Member</h4>
                </Row>
                <Row>
                    <Col md="6">
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>First Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={firstName} disabled={!canEdit}
                                        onChange={(e) => setFirstName(e.currentTarget.value)}
                                        valid={IsInputTextValid(firstName)}
                                        invalid={IsInputTextInvalid(firstName)} />
                                    <FormFeedback>First name is required.</FormFeedback>
                                </Col>
                            </Row>

                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Last Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={lastName} disabled={!canEdit}
                                        onChange={(e) => setLastName(e.currentTarget.value)}
                                        valid={IsInputTextValid(lastName)}
                                        invalid={IsInputTextInvalid(lastName)} />
                                    <FormFeedback>Last name is required.</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Title:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={title} disabled={!canEdit}
                                        onChange={(e) => setTitle(e.currentTarget.value)} />
                                </Col>
                            </Row>
                        </FormGroup>
                        <Row>
                            <Col>
                                <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                                    type="grow" color="warning"
                                    hidden={isLoadingUserData == false} />

                                <div hidden={isLoadingUserData == true}>
                                    <Button
                                        id="createStaff"
                                        onClick={handleAddStaffClick}
                                        className="taxedit"
                                        disabled={!IsValidStaffForm()}
Z                                     >Create Staff Member</Button>

                                    <Button
                                        style={{ marginLeft: "2em" }}
                                        color="danger"
                                        onClick={handleCancelClick}
                                    >Cancel</Button>
                                </div>
                            </Col>

                        </Row>
                    </Col>
                    <Col>
                        <FormGroup>
                                <Row>
                                    <Col >
                                        <Label>Starting Date:  </Label>
                                    </Col>
                                    <Col md="9" >
                                        <DatePicker
                                            selected={startDate}
                                            dateFormat="MM/dd/yyyy"
                                            onChange={handleContactStartDate}
                                        />
                                    </Col>
                                </Row>
                        </FormGroup>
                    </Col>
                </Row>
            </Form>

        </React.Fragment>
    );

    const editStaffModeTemplate = (
        <React.Fragment>
            <Form>
                <Row>
                    <h4>Department Staff Member</h4>
                </Row>
                <Row>
                    <Col md="6">
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>First Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={firstName} disabled={true} />
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <Label>Last Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={lastName} disabled={true} />
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Title:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={title} disabled={!canEdit}
                                        onChange={(e) => setTitle(e.currentTarget.value)}
                                        valid={IsInputTextValid(title)}
                                        invalid={IsInputTextInvalid(title)} />
                                    <FormFeedback>Title is required.</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>
                        <Row>
                            <Col>
                                <Row>
                                    <Col>
                                        <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                                            type="grow" color="warning"
                                            hidden={isLoadingUserData == false} />

                                        <div hidden={isLoadingUserData == true}>
                                            <Button type="button" onClick={(e) => handleSaveStaffButtonClick(true)}
                                                className="taxedit"
                                                disabled={!IsValidStaffForm()}
                                            >
                                                Save
                                            </Button>

                                            <Button
                                                style={{ marginLeft: "2em" }}
                                                color="danger"
                                                onClick={handleCancelClick}>
                                                Cancel
                                            </Button>

                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Starting Date:  {startDate ? startDate.toLocaleDateString() : ""}</Label>
                                </Col>
                            </Row>
                            <Row>
                                <Col >
                                    <Label>Ending Date:  </Label>
                                </Col>
                                <Col md="9">
                                    <DatePicker
                                        selected={endDate}
                                        dateFormat="MM/dd/yyyy"
                                        onChange={handleContactEndDate}
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                    </Col>
                </Row>
            </Form>
        </React.Fragment>
    );

    React.useEffect(() => {
        if (entitySelector != null) {
            if (entitySelector.code != currentEntityCodeSelector) {
                dispatch(getEntityContacts(entitySelector.code));
                dispatch(setEntityCode(entitySelector.code));
                clearInputState();
                //dispatch(setEntityCode(entitySelector.code));
                if (props.isStaff) {
                    setEditMode('LANDING_STAFF');
                } else {
                    setEditMode('LANDING');
                }
            }
            setAfrEnabled(false);
            setCourtReporting939Enabled(false);
            setCourtReporting29Enabled(false);
            if (entitySelector.entityReportAccessChanges) {
                var afrAccess = entitySelector.entityReportAccessChanges.find((ele: EntityReportAccessChange) => ele.state == "AFR_ENABLED");
                if (afrAccess) {
                    if (afrAccess.value == "TRUE") {
                        if (utilityHelper.IsAuthorizedGivenContactRoleUserRoleReportAccess(userRoleSelector, entitySelector,
                            authorizedEntitiesSelector, EntityContactRole.PRIMARY, EntityUserRole.EDITOR, ReportAccessEnum.AFR)) {
                            setAfrEnabled(true);
                        }                       
                    }
                }
                var courtReporting939Access = entitySelector.entityReportAccessChanges.find((ele: EntityReportAccessChange) => ele.state == "COURT_REPORTING_939_ENABLED");
                if (courtReporting939Access) {
                    if (courtReporting939Access.value == "TRUE") {
                        if (utilityHelper.IsAuthorizedGivenContactRoleUserRoleReportAccess(userRoleSelector, entitySelector,
                            authorizedEntitiesSelector, EntityContactRole.PRIMARY, EntityUserRole.EDITOR, ReportAccessEnum.NINE_THIRTY_NINE)) {
                            setCourtReporting939Enabled(true);
                        }  
                    }
                }
                var courtReporting29Access = entitySelector.entityReportAccessChanges.find((ele: EntityReportAccessChange) => ele.state == "COURT_REPORTING_29_ENABLED");
                if (courtReporting29Access) {
                    if (courtReporting29Access.value == "TRUE") {
                        if (utilityHelper.IsAuthorizedGivenContactRoleUserRoleReportAccess(userRoleSelector, entitySelector,
                            authorizedEntitiesSelector, EntityContactRole.PRIMARY, EntityUserRole.EDITOR, ReportAccessEnum.TWENTY_NINE)) {
                            setCourtReporting29Enabled(true);
                        }                      
                    }
                }
            }
        } else {
            dispatch(clearState());
        }
    }, [entitySelector])

    React.useEffect(() => {
        if (entityContact) {
            setStreet(entityContact.street);
            setZipCode(entityContact.zip);
            setPobox(entityContact.pobox);
            setState(null);
            setCounty(null);
            setCityCode(entityContact.cityCode);
        } else {
            setStreet(null);
            setZipCode(null);
            setPobox(null);
            setState(null);
            setCounty(null);
            setCityCode(null);
        }
    }, [entityContact])

    React.useEffect(() => {
        tabulatorRef.current = new Tabulator("#entityContacts", {
            data: contactListData,
            columns: props.isStaff ? staffColumns : defaultColumns,
            layout: "fitColumns",
            dataTree: true,
            dataTreeStartExpanded: false
        }).on("rowClick", rowClick);

    }, [, contactListData])



    React.useEffect(() => {

        if (userRoleSelector && userRoleSelector == "ADMIN") {
            setCanEditEntity(true);
        }
        else {
            if (entitySelector && authorizedEntitiesSelector) {

                var hasSomePrimaryEditorPrivledge = authorizedEntitiesSelector.some((element: any) => element.entity.code == entitySelector.code
                    && element.entityContactRole == EntityContactRole.PRIMARY.toString()
                    && element.entityUserRole == EntityUserRole.EDITOR.toString());

                setCanEditEntity(hasSomePrimaryEditorPrivledge);
               
            } else {
                setCanEditEntity(false);
            }
        }

    }, [entitySelector]);

    return (
        <React.Fragment>
        {
            props.isStaff
                ?
                <Col>
                    <Row>
                        <Col><br /></Col>
                    </Row>
                    <Row>
                        <Col>
                            <Collapse isOpen={editMode == 'LANDING'}>
                                {landingModeTemplate}
                            </Collapse>
                            <Collapse isOpen={editMode == 'LANDING_STAFF'}>
                                {landingModeStaffTemplate}
                            </Collapse>
                            <Collapse isOpen={editMode == 'CREATE_USER'}>
                                {createUserModeTemplate}
                            </Collapse>
                            <Collapse isOpen={editMode == 'CREATE_STAFF'}>
                                {createStaffModeTemplate}
                            </Collapse>
                            <Collapse isOpen={editMode == 'EDIT_USER'}>
                                {editUserModeTemplate}
                            </Collapse>
                            <Collapse isOpen={editMode == 'EDIT_STAFF'}>
                                {editStaffModeTemplate}
                            </Collapse>
                        </Col>
                    </Row>
                    <Row>
                        <Col>

                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <div id="entityContacts" ref={ele => { tabulatorRef.current = ele }} />
                        </Col>
                    </Row>
                </Col>
                    :
                    <Collapse isOpen={ canEditEntity == true }>
                        <Col>
                            <Row>
                                <Col><br /></Col>
                            </Row>
                            <Row>
                                <Col>
                                    <div id="entityContacts" ref={ele => { tabulatorRef.current = ele }} />
                                </Col>
                            </Row>
                            <Row>
                                <Col>

                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <Collapse isOpen={editMode == 'LANDING'}>
                                        {landingModeTemplate}
                                    </Collapse>
                                    <Collapse isOpen={editMode == 'LANDING_STAFF'}>
                                        {landingModeStaffTemplate}
                                    </Collapse>
                                    <Collapse isOpen={editMode == 'CREATE_USER'}>
                                        {createUserModeTemplate}
                                    </Collapse>
                                    <Collapse isOpen={editMode == 'CREATE_STAFF'}>
                                        {createStaffModeTemplate}
                                    </Collapse>
                                    <Collapse isOpen={editMode == 'EDIT_USER'}>
                                        {editUserModeTemplate}
                                    </Collapse>
                                    <Collapse isOpen={editMode == 'EDIT_STAFF'}>
                                        {editStaffModeTemplate}
                                    </Collapse>
                                </Col>
                            </Row>
                        </Col>
                    
                    </Collapse>
                        
                   
                
            }
        </React.Fragment>
    );

}


export default EntityContactsManager;




